import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Briefcase, X, Users, Star, ClipboardList, Calendar, Plus, Loader2, Check, Edit3, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { SearchableSelect, SearchableSelectItem } from '@/components/ui/searchable-select';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { cn } from '@/lib/utils';
import { useConsultingMode } from '@/contexts/ConsultingModeContext';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useUserRoles } from '@/hooks/useUserRoles';
import { useBeratungSessions, useCreateBeratungSession, useUpdateBeratungSession } from '@/hooks/useBeratungSessions';
import { Input } from '@/components/ui/input';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useKunden } from '@/hooks/useKunden';
import { useLeads } from '@/hooks/useLeads';
import { format } from 'date-fns';

export const ConsultingModeToggle = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isAdmin } = useUserRoles();
  const {
    isConsultingMode,
    toggleConsultingMode,
    exitConsultingMode,
    selectedCustomerIds,
    selectedLeadIds,
    setSelectedCustomerIds,
    setSelectedLeadIds,
  } = useConsultingMode();

  const [open, setOpen] = useState(false);
  
  // Search state for combined customer/lead search
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  
  // Local cache for selected items (to display names)
  const [selectedItems, setSelectedItems] = useState<Map<string, { type: 'kunde' | 'lead'; name: string }>>(new Map());
  
  // Primary participant for starting consultation
  const [primaryParticipant, setPrimaryParticipant] = useState<string | null>(null);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Server-side search for customers and leads
  const { data: searchedCustomers = [], isLoading: isLoadingCustomers } = useKunden({
    search: debouncedSearch,
    minSearchLength: 2,
  });

  const { data: searchedLeads = [], isLoading: isLoadingLeads } = useLeads({
    search: debouncedSearch,
    minSearchLength: 2,
  });

  const isSearching = isLoadingCustomers || isLoadingLeads;
  const hasSearchResults = debouncedSearch.length >= 2;

  // Parse primary participant
  const parsedPrimary = useMemo(() => {
    if (!primaryParticipant) return null;
    const [type, id] = primaryParticipant.split(':');
    return { type: type as 'kunde' | 'lead', id };
  }, [primaryParticipant]);

  // Load existing sessions for the primary participant
  const { data: existingSessions = [] } = useBeratungSessions({
    kundeId: parsedPrimary?.type === 'kunde' ? parsedPrimary.id : undefined,
    status: ['aktiv', 'pausiert'],
    enabled: isConsultingMode && !!parsedPrimary && parsedPrimary.type === 'kunde',
  });

  const createSession = useCreateBeratungSession();
  const updateSession = useUpdateBeratungSession();
  
  // State for inline renaming sessions
  const [editingSessionId, setEditingSessionId] = useState<string | null>(null);
  const [editingSessionName, setEditingSessionName] = useState('');

  // Handle selection toggle
  const handleToggleSelection = useCallback((id: string, type: 'kunde' | 'lead', name: string) => {
    const key = `${type}:${id}`;
    
    if (type === 'kunde') {
      if (selectedCustomerIds.includes(id)) {
        setSelectedCustomerIds(selectedCustomerIds.filter(cid => cid !== id));
        setSelectedItems(prev => {
          const next = new Map(prev);
          next.delete(key);
          return next;
        });
      } else {
        setSelectedCustomerIds([...selectedCustomerIds, id]);
        setSelectedItems(prev => new Map(prev).set(key, { type, name }));
      }
    } else {
      if (selectedLeadIds.includes(id)) {
        setSelectedLeadIds(selectedLeadIds.filter(lid => lid !== id));
        setSelectedItems(prev => {
          const next = new Map(prev);
          next.delete(key);
          return next;
        });
      } else {
        setSelectedLeadIds([...selectedLeadIds, id]);
        setSelectedItems(prev => new Map(prev).set(key, { type, name }));
      }
    }
  }, [selectedCustomerIds, selectedLeadIds, setSelectedCustomerIds, setSelectedLeadIds]);

  // Auto-assign primary participant when only one is selected
  useEffect(() => {
    const totalCount = selectedCustomerIds.length + selectedLeadIds.length;
    
    if (totalCount === 1) {
      if (selectedCustomerIds.length === 1) {
        setPrimaryParticipant(`kunde:${selectedCustomerIds[0]}`);
      } else if (selectedLeadIds.length === 1) {
        setPrimaryParticipant(`lead:${selectedLeadIds[0]}`);
      }
    } else if (primaryParticipant) {
      const isStillSelected = parsedPrimary?.type === 'kunde'
        ? selectedCustomerIds.includes(parsedPrimary.id)
        : selectedLeadIds.includes(parsedPrimary?.id || '');
      
      if (!isStillSelected) {
        setPrimaryParticipant(null);
      }
    }
  }, [selectedCustomerIds, selectedLeadIds, primaryParticipant, parsedPrimary]);

  const totalSelected = selectedCustomerIds.length + selectedLeadIds.length;

  // Combined list for primary participant selection
  const primaryParticipantItems = useMemo(() => {
    const items: SearchableSelectItem[] = [];
    
    selectedCustomerIds.forEach(id => {
      const key = `kunde:${id}`;
      const cached = selectedItems.get(key);
      items.push({
        value: key,
        label: cached?.name || 'Kunde',
        group: 'Kunden',
      });
    });
    
    selectedLeadIds.forEach(id => {
      const key = `lead:${id}`;
      const cached = selectedItems.get(key);
      items.push({
        value: key,
        label: cached?.name || 'Lead',
        group: 'Leads',
      });
    });
    
    return items;
  }, [selectedCustomerIds, selectedLeadIds, selectedItems]);

  // Start new consultation
  const handleStartNewBeratung = async () => {
    if (!parsedPrimary) return;
    
    try {
      const teilnehmer: Array<{ id: string; typ: 'kunde' | 'lead' | 'kind' | 'partner'; name: string }> = [];
      
      // Add primary participant first
      const primaryKey = `${parsedPrimary.type}:${parsedPrimary.id}`;
      const primaryCached = selectedItems.get(primaryKey);
      
      teilnehmer.push({
        id: parsedPrimary.id,
        typ: parsedPrimary.type,
        name: primaryCached?.name || 'Unbekannt',
      });
      
      // Add other selected customers
      selectedCustomerIds
        .filter(id => !(parsedPrimary.type === 'kunde' && id === parsedPrimary.id))
        .forEach(id => {
          const cached = selectedItems.get(`kunde:${id}`);
          teilnehmer.push({
            id,
            typ: 'kunde',
            name: cached?.name || 'Unbekannt',
          });
        });
      
      // Add other selected leads
      selectedLeadIds
        .filter(id => !(parsedPrimary.type === 'lead' && id === parsedPrimary.id))
        .forEach(id => {
          const cached = selectedItems.get(`lead:${id}`);
          teilnehmer.push({
            id,
            typ: 'lead',
            name: cached?.name || 'Unbekannt',
          });
        });
      
      const result = await createSession.mutateAsync({
        teilnehmer,
        kundentyp: 'Privat',
      });
      
      setOpen(false);
      navigate(`/beratung/${result.id}`);
    } catch (error) {
      console.error('Error starting consultation:', error);
    }
  };

  // Open existing session
  const handleOpenExistingSession = (sessionId: string) => {
    setOpen(false);
    navigate(`/beratung/${sessionId}`);
  };

  // Rename session handler
  const handleStartRenameSession = (e: React.MouseEvent, sessionId: string, currentName: string | null) => {
    e.stopPropagation();
    setEditingSessionId(sessionId);
    setEditingSessionName(currentName || '');
  };

  const handleSaveSessionName = async (sessionId: string) => {
    if (editingSessionName.trim()) {
      await updateSession.mutateAsync({
        id: sessionId,
        name: editingSessionName.trim(),
      });
    }
    setEditingSessionId(null);
    setEditingSessionName('');
  };

  const handleCancelRename = () => {
    setEditingSessionId(null);
    setEditingSessionName('');
  };

  // Only render for admins - MUST be after ALL hooks
  if (!isAdmin) {
    return null;
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={cn(
            "h-10 w-10 flex-shrink-0 relative transition-colors",
            isConsultingMode && "text-primary ring-2 ring-primary ring-offset-2 ring-offset-background"
          )}
          title={isConsultingMode ? "Beratungsmodus aktiv" : "Beratungsmodus"}
        >
          <Briefcase className="h-5 w-5" />
          {isConsultingMode && totalSelected > 0 && (
            <Badge 
              variant="default" 
              className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center text-xs"
            >
              {totalSelected}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[420px] max-h-[80vh] overflow-y-auto" align="end">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <h4 className="font-medium">Beratungsmodus</h4>
              <p className="text-xs text-muted-foreground">
                Fokussierte Ansicht für Kundentermine
              </p>
            </div>
            <Switch
              checked={isConsultingMode}
              onCheckedChange={toggleConsultingMode}
            />
          </div>

          {isConsultingMode && (
            <>
              <Separator />
              
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label className="text-sm flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Kunde / Lead auswählen
                  </Label>
                  <Command className="rounded-lg border" shouldFilter={false}>
                    <CommandInput
                      placeholder="Mind. 2 Zeichen eingeben..."
                      value={searchInput}
                      onValueChange={setSearchInput}
                    />
                    <CommandList className="max-h-48">
                      {isSearching && (
                        <div className="flex items-center justify-center py-4">
                          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                        </div>
                      )}
                      {!isSearching && hasSearchResults && searchedCustomers.length === 0 && searchedLeads.length === 0 && (
                        <CommandEmpty>Keine Ergebnisse gefunden.</CommandEmpty>
                      )}
                      {!isSearching && !hasSearchResults && (
                        <div className="py-4 text-center text-sm text-muted-foreground">
                          Geben Sie mindestens 2 Zeichen ein...
                        </div>
                      )}
                      {!isSearching && hasSearchResults && searchedCustomers.length > 0 && (
                        <CommandGroup heading="Kunden">
                          {searchedCustomers.slice(0, 10).map(kunde => {
                            const displayName = kunde.firmenname || `${kunde.vorname || ''} ${kunde.zuname || ''}`.trim() || 'Unbenannt';
                            const isSelected = selectedCustomerIds.includes(kunde.id);
                            return (
                              <CommandItem
                                key={kunde.id}
                                value={kunde.id}
                                onSelect={() => handleToggleSelection(kunde.id, 'kunde', displayName)}
                                className="cursor-pointer"
                              >
                                <Check className={cn("mr-2 h-4 w-4", isSelected ? "opacity-100" : "opacity-0")} />
                                {displayName}
                              </CommandItem>
                            );
                          })}
                        </CommandGroup>
                      )}
                      {!isSearching && hasSearchResults && searchedLeads.length > 0 && (
                        <CommandGroup heading="Leads">
                          {searchedLeads.slice(0, 10).map(lead => {
                            const displayName = lead.name || 'Unbenannt';
                            const isSelected = selectedLeadIds.includes(lead.id);
                            return (
                              <CommandItem
                                key={lead.id}
                                value={lead.id}
                                onSelect={() => handleToggleSelection(lead.id, 'lead', displayName)}
                                className="cursor-pointer"
                              >
                                <Check className={cn("mr-2 h-4 w-4", isSelected ? "opacity-100" : "opacity-0")} />
                                {displayName}
                              </CommandItem>
                            );
                          })}
                        </CommandGroup>
                      )}
                    </CommandList>
                  </Command>
                </div>

                {totalSelected > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {Array.from(selectedItems.entries()).map(([key, item]) => (
                      <Badge
                        key={key}
                        variant="secondary"
                        className="cursor-pointer hover:bg-destructive hover:text-destructive-foreground"
                        onClick={() => {
                          const [type, id] = key.split(':');
                          handleToggleSelection(id, type as 'kunde' | 'lead', item.name);
                        }}
                      >
                        {item.name}
                        <X className="ml-1 h-3 w-3" />
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              {/* Primary Participant Selection */}
              {totalSelected > 0 && (
                <>
                  <Separator />
                  <div className="space-y-2">
                    <Label className="text-sm flex items-center gap-2">
                      <Star className="h-4 w-4" />
                      Gesprächspartner 1
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Von wem startet die Beratung?
                    </p>
                    <SearchableSelect
                      items={primaryParticipantItems}
                      value={primaryParticipant || undefined}
                      onValueChange={(val) => setPrimaryParticipant(val || null)}
                      placeholder="Teilnehmer auswählen..."
                      allowClear
                    />
                  </div>
                </>
              )}

              {/* Existing Sessions */}
              {parsedPrimary && existingSessions.length > 0 && (
                <>
                  <Separator />
                  <div className="space-y-2">
                    <Label className="text-sm flex items-center gap-2">
                      <ClipboardList className="h-4 w-4" />
                      Bestehende Beratungen
                    </Label>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {existingSessions.map(session => (
                        <Tooltip key={session.id}>
                          <TooltipTrigger asChild>
                            <div 
                              className="group flex items-start justify-between p-2.5 rounded-md border bg-muted/50 hover:bg-primary/10 hover:border-primary/50 cursor-pointer transition-all"
                              onClick={() => editingSessionId !== session.id && handleOpenExistingSession(session.id)}
                            >
                              <div className="flex items-start gap-2 text-sm min-w-0 flex-1">
                                <Calendar className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                                <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                                  {editingSessionId === session.id ? (
                                    <Input
                                      value={editingSessionName}
                                      onChange={(e) => setEditingSessionName(e.target.value)}
                                      onClick={(e) => e.stopPropagation()}
                                      onKeyDown={(e) => {
                                        e.stopPropagation();
                                        if (e.key === 'Enter') handleSaveSessionName(session.id);
                                        if (e.key === 'Escape') handleCancelRename();
                                      }}
                                      onBlur={() => handleSaveSessionName(session.id)}
                                      placeholder="Name eingeben..."
                                      className="h-6 text-xs flex-1"
                                      autoFocus
                                    />
                                  ) : (
                                    <div className="flex items-center gap-1.5">
                                      <span className="line-clamp-2 font-medium break-words flex-1">
                                        {session.name || 'Beratung'}
                                      </span>
                                      <button
                                        onClick={(e) => handleStartRenameSession(e, session.id, session.name)}
                                        className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 hover:text-primary shrink-0"
                                        title="Umbenennen"
                                      >
                                        <Edit3 className="h-3 w-3" />
                                      </button>
                                    </div>
                                  )}
                                  <span className="text-xs text-muted-foreground">
                                    {format(new Date(session.gestartet_am), 'dd.MM.yyyy, HH:mm')} Uhr ({session.ausgewaehlte_module.length} Module)
                                  </span>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 shrink-0 ml-2">
                                <Badge variant={session.status === 'aktiv' ? 'default' : 'secondary'} className="text-xs">
                                  {session.status === 'aktiv' ? 'Aktiv' : 'Pausiert'}
                                </Badge>
                                <Play className="h-4 w-4 text-primary" />
                              </div>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent side="left">
                            <p>Klicken um Beratung fortzusetzen</p>
                          </TooltipContent>
                        </Tooltip>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* Start New Consultation Button */}
              {parsedPrimary && (
                <>
                  <Separator />
                  <Button
                    variant="default"
                    size="sm"
                    className="w-full"
                    onClick={handleStartNewBeratung}
                    disabled={createSession.isPending}
                  >
                    {createSession.isPending ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Plus className="h-4 w-4 mr-2" />
                    )}
                    Neue Beratung starten
                    <span className="text-xs ml-1 opacity-70">
                      ({totalSelected} Teilnehmer)
                    </span>
                  </Button>
                </>
              )}

              <Separator />

              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => {
                  exitConsultingMode();
                  setOpen(false);
                }}
              >
                <X className="h-4 w-4 mr-2" />
                Beratung beenden
              </Button>
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
